export type AnalyticsTrackProperties = Record<string, unknown>;

export type PinpointCompatibleAttributes = Record<string, string>;
export type PinpointCompatibleMetrics = Record<string, number>;
export type PinpointCompatibleMetadata = Record<string, unknown>;

export interface PinpointCompatibleEventMetadata {
    attributes?: AnalyticsTrackProperties;
    metrics?: AnalyticsTrackProperties;
    eventTimestamp?: number | null;
    arrivalTimestamp?: number | null;
    eventVersion?: string | null;
    application?: PinpointCompatibleMetadata | null;
    client?: PinpointCompatibleMetadata | null;
    device?: PinpointCompatibleMetadata | null;
    session?: PinpointCompatibleMetadata | null;
    clientContext?: PinpointCompatibleMetadata | null;
    awsAccountId?: string | null;
    endpoint?: PinpointCompatibleMetadata | null;
}

export interface PinpointCompatibleEvent {
    event_type: string;
    event_timestamp: number | null;
    arrival_timestamp: number | null;
    event_version: string | null;
    application: PinpointCompatibleMetadata | null;
    client: PinpointCompatibleMetadata | null;
    device: PinpointCompatibleMetadata | null;
    session: PinpointCompatibleMetadata | null;
    attributes: PinpointCompatibleAttributes;
    client_context: PinpointCompatibleMetadata | null;
    awsaccountid: string | null;
    endpoint: PinpointCompatibleMetadata | null;
    metrics: PinpointCompatibleMetrics;
}

interface SplitTrackProperties {
    attributes: PinpointCompatibleAttributes;
    metrics: PinpointCompatibleMetrics;
}

export function buildPinpointCompatibleEvent(
    eventType: string,
    properties: AnalyticsTrackProperties = {},
    metadata: PinpointCompatibleEventMetadata = {},
): PinpointCompatibleEvent {
    const {attributes: baseAttributes} = splitTrackProperties(metadata.attributes ?? {});
    const {metrics: baseMetrics} = splitTrackProperties(metadata.metrics ?? {});
    const {attributes: trackAttributes, metrics: trackMetrics} = splitTrackProperties(properties);

    return {
        event_type: eventType,
        event_timestamp: metadata.eventTimestamp ?? null,
        arrival_timestamp: metadata.arrivalTimestamp ?? null,
        event_version: metadata.eventVersion ?? null,
        application: metadata.application ?? null,
        client: metadata.client ?? null,
        device: metadata.device ?? null,
        session: metadata.session ?? null,
        attributes: {...baseAttributes, ...trackAttributes},
        client_context: metadata.clientContext ?? null,
        awsaccountid: metadata.awsAccountId ?? null,
        endpoint: metadata.endpoint ?? null,
        metrics: {...baseMetrics, ...trackMetrics},
    };
}

function splitTrackProperties(properties: AnalyticsTrackProperties): SplitTrackProperties {
    return Object.entries(properties).reduce<SplitTrackProperties>(
        (eventProperties, [key, value]) => {
            const normalizedKey = normalizePinpointKey(key);

            if (typeof value === "number") {
                eventProperties.metrics[normalizedKey] = value;
            }

            if (typeof value === "string" || typeof value === "boolean") {
                eventProperties.attributes[normalizedKey] = value.toString().slice(0, 200);
            }

            return eventProperties;
        },
        {attributes: {}, metrics: {}},
    );
}

function normalizePinpointKey(key: string): string {
    return key.toLowerCase();
}
